/**
 * Usage Chart Component
 * 
 * Displays interactive line/area charts for token usage over time
 * using Plotly.js for visualization.
 * 
 * Implements Requirement 5.3: Display usage data in interactive charts
 */

import React, { useEffect, useRef } from 'react';
import Plotly from 'plotly.js-dist-min';
import { ChartData } from '../../services/QuotaAnalyticsService';
import styles from './UsageChart.module.css';

interface UsageChartProps {
  chartData: ChartData;
}

export const UsageChart: React.FC<UsageChartProps> = ({ chartData }) => {
  const chartRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!chartRef.current || !chartData.labels.length) return;

    const traces: Plotly.Data[] = chartData.datasets.map((dataset) => ({
      x: chartData.labels,
      y: dataset.data,
      type: 'scatter',
      mode: 'lines+markers',
      name: dataset.label,
      line: {
        color: dataset.borderColor || '#ffffff',
        width: 2.5,
        shape: 'spline',
        smoothing: 1.2
      },
      marker: {
        color: dataset.borderColor || '#ffffff',
        size: 5,
        line: {
          color: 'rgba(255,255,255,0.8)',
          width: 1
        }
      },
      fill: 'tozeroy',
      fillcolor: 'rgba(255, 255, 255, 0.08)',
      hovertemplate: '<b>%{y:,.0f}</b> tokens<extra></extra>'
    }));

    const layout: Partial<Plotly.Layout> = {
      autosize: true,
      height: 280,
      margin: { l: 60, r: 20, t: 10, b: 50 },
      paper_bgcolor: 'transparent',
      plot_bgcolor: 'transparent',
      font: {
        family: 'Inter, system-ui, sans-serif',
        color: '#9ca3af'
      },
      xaxis: {
        title: {
          text: chartData.x_axis_label,
          font: { size: 11, color: '#6b7280' },
          standoff: 10
        },
        gridcolor: 'rgba(75, 85, 99, 0.3)',
        gridwidth: 1,
        linecolor: 'rgba(75, 85, 99, 0.5)',
        tickfont: { size: 10, color: '#9ca3af' },
        showgrid: true,
        zeroline: false,
        tickangle: -45,
        dtick: chartData.labels.length > 12 ? 2 : 1
      },
      yaxis: {
        title: {
          text: chartData.y_axis_label,
          font: { size: 11, color: '#6b7280' },
          standoff: 10
        },
        gridcolor: 'rgba(75, 85, 99, 0.2)',
        gridwidth: 1,
        linecolor: 'rgba(75, 85, 99, 0.5)',
        tickfont: { size: 10, color: '#9ca3af' },
        showgrid: true,
        zeroline: false,
        tickformat: '~s',
        rangemode: 'tozero'
      },
      showlegend: chartData.datasets.length > 1,
      legend: {
        orientation: 'h',
        y: -0.25,
        x: 0.5,
        xanchor: 'center',
        font: { size: 11 }
      },
      hovermode: 'x',
      hoverlabel: {
        bgcolor: 'rgba(17, 24, 39, 0.95)',
        bordercolor: 'rgba(255, 255, 255, 0.4)',
        font: { 
          family: 'Inter, system-ui, sans-serif',
          size: 12,
          color: '#f3f4f6'
        },
        align: 'left'
      }
    };

    const config: Partial<Plotly.Config> = {
      responsive: true,
      displayModeBar: false,
      staticPlot: false
    };

    Plotly.newPlot(chartRef.current, traces, layout, config);

    // Cleanup - copy ref to local variable to avoid stale closure
    const currentChartRef = chartRef.current;
    return () => {
      if (currentChartRef) {
        Plotly.purge(currentChartRef);
      }
    };
  }, [chartData]);

  if (!chartData.labels.length) {
    return (
      <div className={styles.noData}>
        <p>No data available</p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div ref={chartRef} className={styles.chart} />
      <div className={styles.summary}>
        <span className={styles.dataPoints}>
          {chartData.total_data_points} data points
        </span>
      </div>
    </div>
  );
};

export default UsageChart;
